import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { COLOR } from '../../../../packages/constants';
import { camelToKebab } from '../../../../../Scripts/Spotlight/Components/CamelToKebab';
import {
    AccountChildrenType,
    AccountsEliminationsActions,
    AccountsEliminationsActionTypes,
    AccountsEliminationsState,
    AccountType,
    columnWidths,
    DataGridModes,
    DetailsColumns,
    ExpansionMode,
    isExpanded,
} from '../../data-grid';
import { AccountCellInput, SaveCallbackFn } from '../account-cell-input/account-cell-input';
import { AccountCell, TruncateText } from '../account-cell/account-cell';
import { AccountRow, HandleToggleAccountRowFn, ToggleAccountRow } from '../account-row/account-row';
import { StyledElimination, StyledMinus, StyledPlus } from '../icons/icons';

export interface AccountsListProps {
    dataId: string;
    /**
     * The accounts to display
     */
    accounts: AccountType[];
    /**
     * Columns to display within the details pane
     */
    detailsColumns?: DetailsColumns[];
    /**
     * External function to update displayName
     */
    displayNameChange?: SaveCallbackFn;
    /**
     * A callback to
     */
    gridEliminationsDispatch?: any;
    /**
     * Expand/Collapse all rows
     */
    expansionMode?: ExpansionMode;
    /**
     * External function to toggle expand/collapse of a row
     */
    handleToggleAccountRow: HandleToggleAccountRowFn;
    /**
     * Display the eliminated icon on eliminated account rows
     */
    identifyEliminatedAccounts?: boolean;
    /**
     * Show details columns for accounts
     */
    showAccountDetails?: boolean;
    /**
     * The interaction mode of the grid
     */
    mode: DataGridModes;
    accountsEliminationsState?: AccountsEliminationsState;
    accountsEliminationsDispatch?: React.Dispatch<AccountsEliminationsActions>;
}

export const AccountsListTable = styled.table`
    border-top: 1px solid ${COLOR.MAIN.FORM_FIELD};

    ${AccountRow}:first-child ${AccountCell} {
        border-top: none;
    }
`;

const AccountName = styled(TruncateText)`
    width: 258px;
    line-height: 15px;
`;

const AccountChildName = styled(TruncateText)<{ identifyEliminatedAccounts: boolean }>`
    width: ${(props) => (props.identifyEliminatedAccounts ? '252px' : '269px')};
`;

const nbsp = '\u00A0';

const EliminateAccountCheckbox = React.forwardRef(
    (
        {
            dataId,
            account,
            parentAccount,
            checked,
            accountsEliminationsDispatch,
        }: {
            dataId: string;
            account: AccountType | AccountChildrenType;
            parentAccount?: AccountType;
            checked: boolean;
            accountsEliminationsDispatch: any;
        },
        ref: any,
    ) => {
        const update = () => {
            accountsEliminationsDispatch({
                type: AccountsEliminationsActionTypes.TOGGLE_ELIMINATION,
                account: account,
                parentAccount: parentAccount,
            });
        };

        return (
            <input
                ref={ref}
                type="checkbox"
                data-account-id={account.id}
                data-test-id={`${dataId}__checkbox-elimination-${
                    parentAccount
                        ? `${camelToKebab(account.name)}-${camelToKebab(parentAccount.name)}`
                        : camelToKebab(account.name)
                }`}
                checked={checked}
                onChange={update}
            />
        );
    },
);
EliminateAccountCheckbox.displayName = 'EliminateAccountCheckbox';

export const AccountsList = ({
    accounts,
    detailsColumns = [],
    displayNameChange,
    expansionMode = ExpansionMode.expanded,
    handleToggleAccountRow,
    identifyEliminatedAccounts = false,
    showAccountDetails = false,
    mode = DataGridModes.Default,
    dataId,
    accountsEliminationsState,
    accountsEliminationsDispatch,
}: AccountsListProps): JSX.Element => {
    const arrayBuffer = 100; // prevent ref collision by spacing the array, assumes no account has more than this many children
    const checkboxesRef = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        // set the indeterminate state of the checkbox elements
        checkboxesRef.current.forEach((el) => {
            if (!el || el.type !== 'checkbox') {
                return;
            }
            el.indeterminate = false;
            const accountId = el.getAttribute('data-account-id');
            if (
                accountId !== null &&
                accountsEliminationsState &&
                accountsEliminationsState.indeterminateEliminations.includes(accountId)
            ) {
                el.indeterminate = true;
            }
        });
    });

    return (
        <AccountsListTable>
            <tbody>
                {accounts.map((account: AccountType, i: number) => (
                    <React.Fragment key={`accounts-${account.id}`}>
                        <AccountRow>
                            {mode === DataGridModes.EditEliminations && (
                                <td>
                                    <AccountCell
                                        canBeToggled
                                        hasTextAlignedLeft
                                        isGroup={!!account.children.length}
                                        minWidth={columnWidths.eliminations}
                                        isDisplayed={true}
                                    >
                                        <span>
                                            {account.canEliminate ? (
                                                <EliminateAccountCheckbox
                                                    dataId={dataId}
                                                    account={account}
                                                    accountsEliminationsDispatch={accountsEliminationsDispatch}
                                                    checked={
                                                        accountsEliminationsState &&
                                                        accountsEliminationsState.eliminationsSettings.includes(
                                                            account.id,
                                                        )
                                                            ? true
                                                            : false
                                                    }
                                                    ref={(el: HTMLInputElement) => (checkboxesRef.current[i] = el)}
                                                />
                                            ) : (
                                                nbsp
                                            )}
                                        </span>
                                    </AccountCell>
                                </td>
                            )}
                            <td>
                                <AccountCell
                                    isHeader
                                    hasTextAlignedLeft
                                    isGroup={!!account.children.length}
                                    minWidth={305}
                                >
                                    {account.children.length ? (
                                        <ToggleAccountRow
                                            data-account={account.id}
                                            data-test-id={`${dataId}__button-toggle-row-${camelToKebab(account.name)}`}
                                            onClick={() => handleToggleAccountRow(account.id)}
                                        >
                                            <span
                                                className={classNames('icon--toggle-row', {
                                                    'h-display--none': !isExpanded(expansionMode, account),
                                                })}
                                            >
                                                <StyledMinus />
                                            </span>
                                            <span
                                                className={classNames('icon--toggle-row', {
                                                    'h-display--none': isExpanded(expansionMode, account),
                                                })}
                                            >
                                                <StyledPlus />
                                            </span>
                                            {identifyEliminatedAccounts &&
                                                account.children.find((x) => x.isEliminated) && (
                                                    <StyledElimination
                                                        data-test-id={`${dataId}__data-accounts-table__img-${camelToKebab(
                                                            account.name,
                                                        )}-elimination-icon`}
                                                    />
                                                )}
                                            <AccountName
                                                title={account.name}
                                                data-test-id={`${dataId}__data-accounts-table__div-${camelToKebab(
                                                    account.name,
                                                )}-account-name`}
                                            >
                                                {account.name}
                                            </AccountName>
                                        </ToggleAccountRow>
                                    ) : (
                                        <TruncateText title={account.name}>{account.name}</TruncateText>
                                    )}
                                </AccountCell>
                            </td>
                            {detailsColumns.includes(DetailsColumns.ReportCode) && (
                                <td
                                    data-test-id={`${dataId}__data-accounts-table__td-${camelToKebab(
                                        account.name,
                                    )}-report-code`}
                                >
                                    <AccountCell
                                        hasTextAlignedLeft
                                        isGroup={!!account.children.length}
                                        canBeToggled
                                        isDisplayed={showAccountDetails}
                                        minWidth={142}
                                    >
                                        {account.reportCode}
                                    </AccountCell>
                                </td>
                            )}
                            {detailsColumns.includes(DetailsColumns.DisplayName) &&
                                typeof displayNameChange === 'function' && (
                                    <td>
                                        <span>
                                            <AccountCellInput
                                                cellId={account.id}
                                                hasTextAlignedLeft
                                                isGroup={!!account.children.length}
                                                canBeToggled
                                                isDisplayed={showAccountDetails}
                                                hasNoPadding
                                                inputProps={{
                                                    type: 'text',
                                                    value: account.displayName ? account.displayName : '',
                                                    saveFn: displayNameChange,
                                                    dataId: `${dataId}__input-${camelToKebab(
                                                        account.name,
                                                    )}-spotlight-display-name`,
                                                }}
                                            />
                                        </span>
                                    </td>
                                )}
                        </AccountRow>
                        {account.children.map((accountChild: AccountChildrenType, j: number) => (
                            <AccountRow
                                key={`accounts-${account.id}-${j}`}
                                className={classNames(`parent-id-${account.id}`, {
                                    'h-display--none': !isExpanded(expansionMode, account),
                                })}
                            >
                                {mode === DataGridModes.EditEliminations && (
                                    <td>
                                        <AccountCell canBeToggled hasTextAlignedLeft minWidth={12} isDisplayed={true}>
                                            <span>
                                                {accountChild.canEliminate ? (
                                                    <EliminateAccountCheckbox
                                                        dataId={dataId}
                                                        account={accountChild}
                                                        accountsEliminationsDispatch={accountsEliminationsDispatch}
                                                        checked={
                                                            accountsEliminationsState &&
                                                            accountsEliminationsState.eliminationsSettings.includes(
                                                                accountChild.id,
                                                            )
                                                                ? true
                                                                : false
                                                        }
                                                        parentAccount={account}
                                                        ref={(el: any) =>
                                                            (checkboxesRef.current[(i + 1) * arrayBuffer + j] = el)
                                                        }
                                                    />
                                                ) : (
                                                    nbsp
                                                )}
                                            </span>
                                        </AccountCell>
                                    </td>
                                )}
                                <td>
                                    <AccountCell indent minWidth={305}>
                                        {identifyEliminatedAccounts && accountChild.isEliminated && (
                                            <StyledElimination
                                                data-test-id={`${dataId}__data-accounts-table__img-${camelToKebab(
                                                    accountChild.name,
                                                )}-${camelToKebab(account.name)}-elimination-icon`}
                                            />
                                        )}
                                        <AccountChildName
                                            title={accountChild.name}
                                            data-test-id={`${dataId}__data-accounts-table__tbody-${camelToKebab(
                                                accountChild.name,
                                            )}-${camelToKebab(account.name)}`}
                                            identifyEliminatedAccounts={identifyEliminatedAccounts}
                                        >{`${accountChild.name}`}</AccountChildName>
                                    </AccountCell>
                                </td>
                                {detailsColumns.includes(DetailsColumns.ReportCode) && (
                                    <td>
                                        <AccountCell canBeToggled isDisplayed={showAccountDetails}>
                                            {nbsp}
                                        </AccountCell>
                                    </td>
                                )}
                                {detailsColumns.includes(DetailsColumns.DisplayName) &&
                                    typeof displayNameChange === 'function' && (
                                        <td>
                                            <AccountCell minWidth={156} canBeToggled isDisplayed={showAccountDetails}>
                                                {nbsp}
                                            </AccountCell>
                                        </td>
                                    )}
                            </AccountRow>
                        ))}
                    </React.Fragment>
                ))}
            </tbody>
        </AccountsListTable>
    );
};
